import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Calendar, Tag, MapPin, ArrowRight, Search, ChevronLeft, ChevronRight } from "lucide-react";
import Logo from "@/components/Logo";
import FooterCitiesCompact from "@/components/FooterCitiesCompact";
import FloatingWhatsAppBlog from "@/components/FloatingWhatsAppBlog";
import BlogMobileMenu from "@/components/BlogMobileMenu";
import { CITIES } from "@/cityConfig";
import { trackPageView } from "@/utils/tracker";
import LanguageSelector from "@/components/LanguageSelector";
import { translateTag } from "@/utils/tagTranslations";
import { getLangFromUrl, setLangInUrl, useHreflang, updateCanonical, useAutoSyncLangToUrl } from "@/utils/seoLang";
import { BlogHero } from "@/components/BlogHero";

const API = "/api";

function BlogHeader({ lang, setLang, availableLanguages }) {
  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-40" data-testid="blog-header">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3" data-testid="blog-logo-link">
          <Logo variant="dark" />
        </Link>
        <div className="flex items-center gap-4">
          <Link
            to="/blog"
            className="hidden sm:inline text-sm font-bold text-pink-600"
            data-testid="blog-nav-link"
          >
            {lang === "tr" ? "Blog" : (lang === "en" ? "Blog" : "Блог")}
          </Link>
          <LanguageSelector
            selectedLang={lang}
            onLangChange={setLang}
            isScrolled={true}
            availableLanguages={availableLanguages}
          />
          <BlogMobileMenu lang={lang} />
        </div>
      </div>
    </header>
  );
}

function ArticleCard({ article, lang }) {
  const title = lang === "tr" && article.title_tr ? article.title_tr : (lang === "en" && article.title_en ? article.title_en : article.title_ru);
  const excerpt = lang === "tr" && article.excerpt_tr ? article.excerpt_tr : (lang === "en" && article.excerpt_en ? article.excerpt_en : article.excerpt_ru);
  const cityData = article.city ? CITIES[article.city] : null;
  const cityName = cityData ? (lang === "tr" ? (cityData.nameTr || cityData.nameEn) : (lang === "en" ? cityData.nameEn : cityData.name)) : null;
  const date = article.published_at ? new Date(article.published_at).toLocaleDateString(lang === "tr" ? "tr-TR" : (lang === "en" ? "en-US" : "ru-RU"), { year: "numeric", month: "long", day: "numeric" }) : "";

  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col"
      data-testid={`article-card-${article.slug}`}
    >
      {article.cover_image && (
        <div className="aspect-[16/9] overflow-hidden bg-gray-100">
          <img
            src={article.cover_image}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {cityName && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full">
              <MapPin className="w-3 h-3" />
              {cityName}
            </span>
          )}
          {article.tags?.slice(0, 2).map(tag => (
            <span key={tag} className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {translateTag(tag, lang)}
            </span>
          ))}
        </div>
        <h2 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-pink-600 transition-colors line-clamp-2">
          {title}
        </h2>
        {excerpt && (
          <p className="text-sm text-gray-500 mb-4 line-clamp-2 flex-1">{excerpt}</p>
        )}
        <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-50">
          <span className="flex items-center gap-1.5 text-xs text-gray-400">
            <Calendar className="w-3 h-3" />
            {date}
          </span>
          <span className="flex items-center gap-1 text-xs font-semibold text-pink-500 group-hover:gap-2 transition-all">
            {lang === "tr" ? "Oku" : (lang === "en" ? "Read" : "Читать")}
            <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [tags, setTags] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const page = parseInt(searchParams.get("page") || "1");
  const city = searchParams.get("city") || "";
  const tag = searchParams.get("tag") || "";

  const availableLanguages = useMemo(() => {
    if (!city) return ["ru", "en"];
    const cityData = CITIES[city];
    return cityData?.languages || ["ru", "en"];
  }, [city]);

  const [lang, setLangState] = useState(() => getLangFromUrl(searchParams, availableLanguages));

  const setLang = (newLang) => {
    setLangState(newLang);
    setLangInUrl(newLang, searchParams, setSearchParams);
  };

  useAutoSyncLangToUrl(lang, searchParams, setSearchParams);

  // hreflang for blog page
  const blogPath = city ? `/blog?city=${city}` : "/blog";
  useHreflang(blogPath, availableLanguages);

  useEffect(() => {
    const cityData = city ? CITIES[city] : null;
    const cityName = cityData ? (lang === "tr" ? (cityData.nameTr || cityData.nameEn) : (lang === "en" ? cityData.nameEn : cityData.name)) : "";
    const blogTitle = lang === "tr"
      ? `YUCCA Flowers Blog${cityName ? ` — ${cityName}` : ""}`
      : lang === "en"
      ? `YUCCA Flowers Blog${cityName ? ` — ${cityName}` : ""}`
      : `Блог YUCCA Flowers${cityName ? ` — ${cityName}` : ""}`;
    document.title = blogTitle;
    document.documentElement.lang = lang;
    updateCanonical(blogPath, availableLanguages);
  }, [lang, city, blogPath, availableLanguages]);

  useEffect(() => {
    trackPageView("/blog");
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { page, lang };
        if (city) params.city = city;
        if (tag) params.tag = tag;
        const [postsRes, tagsRes] = await Promise.all([
          axios.get(`${API}/blog/posts`, { params }),
          axios.get(`${API}/blog/tags`),
        ]);
        setArticles(postsRes.data.articles);
        setTotal(postsRes.data.total);
        setPages(postsRes.data.pages);
        setTags(tagsRes.data);
      } catch (err) {
        console.error("Blog load error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [page, city, tag, lang]);

  const setFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete("page");
    setSearchParams(params);
  };

  const cityList = Object.values(CITIES);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="blog-page">
      <BlogHeader lang={lang} setLang={setLang} availableLanguages={availableLanguages} />

      <BlogHero lang={lang} city={city} total={total} citiesCount={cityList.length} onCityChange={(v) => setFilter("city", v)} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="blog-filters">

          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {tag && (
                <button
                  onClick={() => setFilter("tag", "")}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold bg-pink-500 text-white"
                  data-testid="blog-clear-tag"
                >
                  {translateTag(tag, lang)} ✕
                </button>
              )}
              {!tag && tags.slice(0, 8).map(t => (
                <button
                  key={t.tag}
                  onClick={() => setFilter("tag", t.tag)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-gray-200 text-gray-600 hover:border-pink-300 hover:text-pink-600 transition-colors"
                  data-testid={`blog-tag-${t.tag}`}
                >
                  {translateTag(t.tag, lang)} ({t.count})
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Articles Grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="w-10 h-10 border-2 border-pink-400 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-16" data-testid="blog-empty">
            <p className="text-gray-500 text-lg mb-2">
              {lang === "tr" ? "Henuz makale yok" : lang === "en" ? "No articles yet" : "Статей пока нет"}
            </p>
            <p className="text-gray-400 text-sm">
              {lang === "tr" ? "Yakinda yeni icerikler icin tekrar gelin!" : lang === "en" ? "Check back soon for new content!" : "Скоро здесь появятся полезные статьи!"}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-articles-grid">
              {articles.map(article => (
                <ArticleCard key={article.id} article={article} lang={lang} />
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10" data-testid="blog-pagination">
                <button
                  onClick={() => setFilter("page", String(page - 1)) || setSearchParams(prev => { prev.set("page", page - 1); return prev; })}
                  disabled={page <= 1}
                  className="p-2 rounded-lg border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium text-gray-600 px-3">
                  {page} / {pages}
                </span>
                <button
                  onClick={() => { const p = new URLSearchParams(searchParams); p.set("page", page + 1); setSearchParams(p); }}
                  disabled={page >= pages}
                  className="p-2 rounded-lg border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10 mt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <Logo variant="light" />
          <p className="text-gray-400 text-sm mt-4">
            &copy; 2024 YUCCA Flowers. {lang === "tr" ? "Tum haklar saklidir." : lang === "en" ? "All rights reserved." : "Все права защищены."}
          </p>
          <div className="flex items-center justify-center gap-4 mt-3 text-sm">
            <Link to="/" className="text-gray-500 hover:text-gray-300 transition-colors">
              {lang === "tr" ? "Ana sayfa" : lang === "en" ? "Home" : "Главная"}
            </Link>
            <Link to="/terms" className="text-gray-500 hover:text-gray-300 transition-colors">
              {lang === "tr" ? "Kosullar" : lang === "en" ? "Terms" : "Соглашение"}
            </Link>
            <Link to="/privacy" className="text-gray-500 hover:text-gray-300 transition-colors">
              {lang === "tr" ? "Gizlilik" : lang === "en" ? "Privacy" : "Конфиденциальность"}
            </Link>
          </div>
          <FooterCitiesCompact lang={lang} />
        </div>
      </footer>
      <FloatingWhatsAppBlog lang={lang} />
    </div>
  );
}
